const documentElement = document.documentElement;

if (documentElement.dataset.returnSitewidePosthog !== 'ready') {
  documentElement.dataset.returnSitewidePosthog = 'ready';
  setupReturnSitewidePosthog();
}

function setupReturnSitewidePosthog() {
  const instrumentationVersion = 'return-sitewide-v1';
  const homepageSections = [
    ['hero', '.hero'],
    ['return_difference', '#why-return'],
    ['ingredients', '#ingredients'],
    ['flavor', '[data-taste-scroll]'],
    ['comparison', '[data-return-glass-comparison]'],
    ['benefits', '#benefits'],
    ['how_it_works', '#how-it-works'],
    ['offer', '#offer'],
    ['reviews', '#reviews'],
    ['quality', '#quality'],
    ['faq', '#faq'],
    ['final_cta', '.final-cta'],
  ];

  const deviceType = () => {
    if (window.matchMedia('(max-width: 767px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 1024px)').matches) return 'tablet';
    return 'desktop';
  };

  const capture = (eventName, properties = {}) => {
    const posthog = window.posthog;
    if (!posthog || typeof posthog.capture !== 'function') return;
    posthog.capture(eventName, {
      page_path: window.location.pathname,
      device_type: deviceType(),
      instrumentation_version: instrumentationVersion,
      ...properties,
    });
  };

  const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, 160);
  const destination = (element) => element instanceof HTMLAnchorElement
    ? element.getAttribute('href') || element.href || undefined
    : undefined;
  const interactionType = () => deviceType() === 'mobile' ? 'tap' : 'click';

  const parseMoney = (value) => {
    if (typeof value !== 'string') return undefined;
    const normalized = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const cartState = (scope = document) => {
    const quantities = [...scope.querySelectorAll('.return-cart-line input[name="updates[]"]')]
      .map((input) => Number.parseInt(input.value, 10))
      .filter(Number.isFinite);
    const lineValues = [...scope.querySelectorAll('.return-cart-line__price text-component')]
      .map((element) => parseMoney(element.textContent || ''))
      .filter(Number.isFinite);
    return {
      cart_quantity: quantities.length ? quantities.reduce((sum, quantity) => sum + quantity, 0) : 0,
      cart_value: lineValues.length ? Number(lineValues.reduce((sum, value) => sum + value, 0).toFixed(2)) : 0,
    };
  };

  const captureCartOpened = (openSource) => {
    const drawer = document.querySelector('cart-drawer-component') || document;
    capture('site_cart_opened', {
      ...cartState(drawer),
      open_source: openSource,
    });
  };

  const classifyHeaderItem = (actionable, inMobileMenu) => {
    if (actionable.closest('.rh3-header__logo')) return { item: 'Return Hydration', item_type: 'logo' };
    if (actionable.closest('.rh-header__cart-link, [data-testid="cart-drawer-trigger"], .action__cart')) {
      return { item: 'Cart', item_type: 'cart' };
    }
    if (actionable.matches('[href*="/account"], [href*="shopify.com/"][href*="account"]')) {
      return { item: cleanText(actionable.textContent) || 'Account', item_type: 'account' };
    }
    if (actionable.closest('.announcement-bar')) {
      return { item: cleanText(actionable.textContent) || 'Announcement', item_type: 'announcement' };
    }
    const mobileMenuLabel = inMobileMenu ? cleanText(actionable.querySelector('span')?.textContent) : '';
    return {
      item: mobileMenuLabel || cleanText(actionable.textContent) || cleanText(actionable.getAttribute('aria-label')) || 'Navigation item',
      item_type: inMobileMenu || actionable.closest('.rh3-header__nav') ? 'nav_link' : 'utility',
    };
  };

  const handleHeaderInteraction = (target) => {
    const announcement = target.closest('.announcement-bar__link');
    const header = target.closest('[data-rh3-header]');
    if (!announcement && !header) return false;

    const menuOpen = target.closest('[data-rh3-menu-open]');
    if (menuOpen) {
      capture('site_mobile_menu_opened');
      return true;
    }

    const actionable = target.closest('a[href], button');
    if (!(actionable instanceof HTMLElement) || actionable.matches('[data-rh3-menu-close]')) return false;
    const cartTrigger = actionable.closest('.rh-header__cart-link, [data-testid="cart-drawer-trigger"], .action__cart');
    if (actionable.closest('cart-drawer-component') && !cartTrigger) return false;
    const inMobileMenu = Boolean(actionable.closest('[data-rh3-mobile-menu]'));
    const item = classifyHeaderItem(actionable, inMobileMenu);

    capture('site_header_interacted', {
      ...item,
      interaction_type: inMobileMenu ? 'mobile_menu' : interactionType(),
      destination: destination(actionable),
      menu_state: inMobileMenu ? 'open' : undefined,
    });

    if (item.item_type === 'cart') captureCartOpened('header_cart');
    return true;
  };

  const classifyFooterItem = (actionable) => {
    const href = actionable.getAttribute('href') || '';
    const navigationLabel = actionable.closest('nav')?.getAttribute('aria-label') || '';
    if (/^mailto:/i.test(href)) return 'email';
    if (/^tel:/i.test(href) || /contact/i.test(href)) return 'support';
    if (/instagram|facebook|tiktok|youtube|pinterest/i.test(href)) return 'social';
    if (/account|subscription/i.test(href)) return 'account';
    if (/legal/i.test(navigationLabel) || /policies|privacy|terms|accessibility|data-sharing|messaging-consent/i.test(href)) return 'legal';
    if (/help/i.test(navigationLabel)) return 'support';
    if (navigationLabel) return 'navigation';
    return 'other';
  };

  const handleFooterInteraction = (target) => {
    const footer = target.closest('.rh3-footer');
    if (!footer) return false;
    const actionable = target.closest('a[href], button');
    if (!(actionable instanceof HTMLElement)) return false;
    capture('site_footer_interacted', {
      item: cleanText(actionable.textContent) || cleanText(actionable.getAttribute('aria-label')) || 'Footer item',
      item_type: classifyFooterItem(actionable),
      destination: destination(actionable),
    });
    return true;
  };

  const homepageSection = (element) => element.closest('[data-posthog-home-section]');

  const homepageSectionProperties = (element) => {
    const section = homepageSection(element);
    return {
      section_name: section?.dataset.posthogHomeSection || 'unknown',
      section_index: Number.parseInt(section?.dataset.posthogHomeSectionIndex || '', 10) || undefined,
    };
  };

  const homepageCtaType = (actionable, href) => {
    if (actionable.closest('[data-sticky-cta]')) return 'sticky_mobile';
    const text = cleanText(actionable.textContent).toLowerCase();
    if (text.includes('subscribe')) return 'subscription';
    if (text.includes('review')) return 'review';
    if (text.includes('learn')) return 'learn_more';
    if (href?.startsWith('#')) return 'anchor';
    if (href) return 'navigation';
    if (actionable.matches('[data-return-primary-action], [data-return-notify]')) return 'product';
    return 'other';
  };

  const productHandleFromHref = (href) => {
    const match = String(href || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const handleHomepageAction = (target) => {
    const root = target.closest('[data-return-home]');
    if (!root) return false;

    const faqSummary = target.closest('#faq summary');
    if (faqSummary instanceof HTMLElement) {
      const details = faqSummary.closest('details');
      const items = [...root.querySelectorAll('#faq details.faq-item')];
      window.requestAnimationFrame(() => {
        if (!(details instanceof HTMLDetailsElement) || !details.open) return;
        capture('homepage_accordion_opened', {
          section_name: 'faq',
          item: cleanText(faqSummary.textContent).replace(/\+\s*$/, '').trim(),
          item_index: items.indexOf(details) + 1,
        });
      });
      return true;
    }

    const mediaControl = target.closest('.review-play');
    if (mediaControl instanceof HTMLElement) {
      capture('homepage_media_interacted', {
        section_name: 'reviews',
        media_type: 'video',
        interaction_type: 'play',
        media_index: [...root.querySelectorAll('.review-card')].indexOf(mediaControl.closest('.review-card')) + 1,
      });
      return true;
    }

    const modalTrigger = target.closest('[data-supplement-facts-open]');
    if (modalTrigger instanceof HTMLElement) {
      capture('homepage_modal_opened', {
        modal_name: 'supplement_facts',
        section_name: 'quality',
        open_source: 'supplement_facts_button',
      });
      return true;
    }

    const carouselControl = target.closest('[data-review-prev], [data-review-next], [data-benefit-carousel-dot], [data-step-carousel-dot]');
    if (carouselControl instanceof HTMLElement) {
      let sectionName = 'reviews';
      let interaction = 'arrow';
      let currentIndex;
      let previousIndex;
      if (carouselControl.matches('[data-benefit-carousel-dot]')) {
        sectionName = 'benefits';
        interaction = 'dot';
        const track = root.querySelector('[data-benefit-carousel]');
        const cards = [...(track?.querySelectorAll('[data-benefit-card]') || [])];
        previousIndex = closestCarouselIndex(track, cards) + 1;
        currentIndex = Number.parseInt(carouselControl.dataset.benefitCarouselDot || '', 10) + 1;
      } else if (carouselControl.matches('[data-step-carousel-dot]')) {
        sectionName = 'how_it_works';
        interaction = 'dot';
        const track = root.querySelector('[data-step-carousel]');
        const cards = [...(track?.querySelectorAll('[data-step-card]') || [])];
        previousIndex = closestCarouselIndex(track, cards) + 1;
        currentIndex = Number.parseInt(carouselControl.dataset.stepCarouselDot || '', 10) + 1;
      } else {
        const track = root.querySelector('[data-review-track]');
        const cards = [...(track?.querySelectorAll('.review-card') || [])];
        const active = closestCarouselIndex(track, cards);
        previousIndex = active + 1;
        currentIndex = Math.max(1, Math.min(cards.length, active + (carouselControl.matches('[data-review-next]') ? 2 : 0)));
      }
      capture('homepage_carousel_interacted', {
        section_name: sectionName,
        interaction_type: interaction,
        current_index: currentIndex,
        previous_index: previousIndex,
      });
      return true;
    }

    const actionable = target.closest('a[href], button');
    if (!(actionable instanceof HTMLElement)) return false;
    if (actionable.closest('[data-rh3-header], .rh3-footer')) return false;
    const href = destination(actionable);
    const productHandle = productHandleFromHref(href);

    const isCta = actionable.matches('a.button, .rh3-button, [data-return-primary-action], [data-return-notify], [data-sticky-cta] a');

    if (productHandle && !isCta) {
      capture('homepage_product_clicked', {
        product_handle: productHandle,
        product_title: productHandle === 'lemon-drop' ? 'Lemon Drop' : cleanText(actionable.textContent),
        click_target: target.closest('img') ? 'image' : actionable.matches('a, button') ? 'cta' : 'card',
        ...homepageSectionProperties(actionable),
        destination: href,
      });
      return true;
    }

    if (!isCta) return false;
    capture('homepage_cta_clicked', {
      cta_text: cleanText(actionable.textContent) || cleanText(actionable.getAttribute('aria-label')),
      ...homepageSectionProperties(actionable),
      cta_type: homepageCtaType(actionable, href),
      destination: href,
    });
    return true;
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (handleHeaderInteraction(event.target)) return;
    if (handleFooterInteraction(event.target)) return;
    handleHomepageAction(event.target);
  }, true);

  document.addEventListener('cart:update', (event) => {
    const itemCount = Number(event.detail?.data?.itemCount || 0);
    const source = String(event.detail?.data?.source || '');
    if (itemCount < 1 || !/lemon-drop-(home|pdp)|return-purchase-form|product-form|quick-add|sticky-add-to-cart/i.test(source)) return;
    window.setTimeout(() => {
      const drawerDialog = document.querySelector('cart-drawer-component dialog');
      if (drawerDialog instanceof HTMLDialogElement && drawerDialog.open) captureCartOpened('add_to_cart');
    }, 100);
  });

  const setupHomepage = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.posthogHomepageInstrumentation === 'ready') return;
    root.dataset.posthogHomepageInstrumentation = 'ready';

    const sections = homepageSections
      .map(([name, selector], index) => {
        const element = root.querySelector(selector);
        if (!(element instanceof HTMLElement)) return null;
        element.dataset.posthogHomeSection = name;
        element.dataset.posthogHomeSectionIndex = String(index + 1);
        return { name, index: index + 1, element };
      })
      .filter(Boolean);

    if ('IntersectionObserver' in window) {
      const viewed = new Set();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const name = entry.target.dataset.posthogHomeSection;
          if (!name || viewed.has(name)) return;
          viewed.add(name);
          observer.unobserve(entry.target);
          const sectionIndex = Number.parseInt(entry.target.dataset.posthogHomeSectionIndex || '', 10);
          capture('homepage_section_viewed', {
            section_name: name,
            section_index: sectionIndex,
          });
          if (window.posthog && typeof window.posthog.register_for_session === 'function') {
            window.posthog.register_for_session({
              last_homepage_section: name,
              last_homepage_section_index: sectionIndex,
            });
          }
        });
      }, { threshold: 0, rootMargin: '-30% 0px -30% 0px' });
      sections.forEach(({ element }) => observer.observe(element));
    }

    const scrollThresholds = [25, 50, 75, 90];
    const reached = new Set();
    let scrollFrame = 0;
    const measureScroll = () => {
      scrollFrame = 0;
      const distance = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      if (!distance) return;
      const progress = Math.min(100, Math.max(0, (window.scrollY / distance) * 100));
      scrollThresholds.forEach((depth) => {
        if (progress < depth || reached.has(depth)) return;
        reached.add(depth);
        capture('homepage_scroll_depth_reached', { depth });
      });
    };
    const requestScrollMeasure = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(measureScroll);
    };
    window.addEventListener('scroll', requestScrollMeasure, { passive: true });
    window.addEventListener('resize', requestScrollMeasure, { passive: true });
    requestScrollMeasure();

    setupHomepageSwipeTracking(root, capture);
  };

  setupHomepage(document.querySelector('[data-return-home]'));
  document.addEventListener('shopify:section:load', () => {
    setupHomepage(document.querySelector('[data-return-home]'));
  });
}

function closestCarouselIndex(track, cards) {
  if (!(track instanceof HTMLElement) || !cards.length) return 0;
  const trackRect = track.getBoundingClientRect();
  return cards.reduce((closest, card, index) => {
    const distance = Math.abs(card.getBoundingClientRect().left - trackRect.left);
    return distance < closest.distance ? { index, distance } : closest;
  }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
}

function setupHomepageSwipeTracking(root, capture) {
  const carousels = [
    { section: 'benefits', track: root.querySelector('[data-benefit-carousel]'), cardSelector: '[data-benefit-card]' },
    { section: 'how_it_works', track: root.querySelector('[data-step-carousel]'), cardSelector: '[data-step-card]' },
    { section: 'reviews', track: root.querySelector('[data-review-track]'), cardSelector: '.review-card' },
  ];

  carousels.forEach(({ section, track, cardSelector }) => {
    if (!(track instanceof HTMLElement) || track.dataset.posthogSwipeTracking === 'ready') return;
    track.dataset.posthogSwipeTracking = 'ready';
    let pointerStart = null;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      const cards = [...track.querySelectorAll(cardSelector)];
      pointerStart = {
        x: event.clientX,
        y: event.clientY,
        index: closestCarouselIndex(track, cards) + 1,
      };
    }, { passive: true });
    track.addEventListener('pointerup', (event) => {
      if (!pointerStart || event.pointerType === 'mouse') return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      const previousIndex = pointerStart.index;
      pointerStart = null;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      window.setTimeout(() => {
        const cards = [...track.querySelectorAll(cardSelector)];
        capture('homepage_carousel_interacted', {
          section_name: section,
          interaction_type: 'swipe',
          current_index: closestCarouselIndex(track, cards) + 1,
          previous_index: previousIndex,
        });
      }, 160);
    }, { passive: true });
    track.addEventListener('pointercancel', () => {
      pointerStart = null;
    }, { passive: true });
    track.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      if (section !== 'reviews' && !window.matchMedia('(max-width: 640px)').matches) return;
      const initialCards = [...track.querySelectorAll(cardSelector)];
      const previousIndex = closestCarouselIndex(track, initialCards) + 1;
      window.setTimeout(() => {
        const cards = [...track.querySelectorAll(cardSelector)];
        capture('homepage_carousel_interacted', {
          section_name: section,
          interaction_type: 'keyboard',
          current_index: closestCarouselIndex(track, cards) + 1,
          previous_index: previousIndex,
        });
      }, 160);
    });
  });
}
